import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdClose, MdEdit, MdLocalShipping, MdPayments, MdRefresh, MdVisibility } from 'react-icons/md';
import { getData, postData } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';
const LIMIT = 10;
const SEGMENTS = [
  { key: 'B2C', label: 'Customers (B2C)', accent: '#6366f1' },
  { key: 'B2B', label: 'Businesses (B2B)', accent: '#0ea5e9' },
];
const FIELD_LABEL = {
  codAvailable: 'Cash on delivery',
  codCharge: 'COD charge',
  codThreshold: 'Max COD order amount',
  deliveryCharge: 'Delivery charge',
  deliveryChargeRemovalThreshold: 'Free delivery above',
};
const DEFAULT_SEG = { codAvailable: true, codCharge: 0, codThreshold: 0, deliveryCharge: 0, deliveryChargeRemovalThreshold: 0 };

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const showVal = (field, v) => (field === 'codAvailable' ? (v ? 'On' : 'Off') : inr(v));
const dt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };

const Row = ({ label, value, muted }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 800, color: muted ? '#94a3b8' : '#0f172a' }}>{value}</Typography>
  </Box>
);

const SegmentCard = ({ seg, data }) => (
  <Box sx={{ flex: 1, minWidth: 300, background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${seg.accent}`, borderRadius: '14px', p: 2.5 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 16, mb: 1.5 }}>{seg.label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <MdPayments color={seg.accent} /><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>Cash on delivery</Typography>
      <Chip size="small" label={data.codAvailable ? 'Available' : 'Disabled'} sx={{ fontWeight: 700, height: 20, background: data.codAvailable ? '#dcfce7' : '#fee2e2', color: data.codAvailable ? '#166534' : '#991b1b' }} />
    </Box>
    <Row label="COD charge" value={inr(data.codCharge)} muted={!data.codAvailable} />
    <Row label="Max COD order" value={data.codThreshold ? inr(data.codThreshold) : 'No limit'} muted={!data.codAvailable} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 0.5 }}>
      <MdLocalShipping color={seg.accent} /><Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>Delivery</Typography>
    </Box>
    <Row label="Delivery charge" value={inr(data.deliveryCharge)} />
    <Row label="Free delivery above" value={data.deliveryChargeRemovalThreshold ? inr(data.deliveryChargeRemovalThreshold) : 'Never free'} />
  </Box>
);

const SegmentForm = ({ seg, value, errors, onChange }) => {
  const num = (field, label, helper) => (
    <TextField fullWidth size="small" type="number" label={label} value={value[field]} onChange={(e) => onChange(field, e.target.value)} error={!!errors[field]} helperText={errors[field] || helper}
      disabled={['codCharge', 'codThreshold'].includes(field) && !value.codAvailable}
      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} inputProps={{ min: 0 }} />
  );
  return (
    <Box sx={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${seg.accent}`, borderRadius: '14px', p: 2.25, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{seg.label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700 }}>Cash on delivery available</Typography>
        <Switch checked={value.codAvailable} onChange={(e) => onChange('codAvailable', e.target.checked)} />
      </Box>
      {num('codCharge', 'COD charge', 'Added to the order when paying on delivery')}
      {num('codThreshold', 'Max COD order amount', '0 means no limit')}
      {num('deliveryCharge', 'Delivery charge', 'Charged on every order below the free limit')}
      {num('deliveryChargeRemovalThreshold', 'Free delivery above', '0 means delivery is never free')}
    </Box>
  );
};

const emptyFilters = { segment: '', field: '', userId: '', from: '', to: '' };

const ShippingPaymentConfig = () => {
  const [settings, setSettings] = useState({ B2C: DEFAULT_SEG, B2B: DEFAULT_SEG });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saved, setSaved] = useState(true);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState('');
  const [loadError, setLoadError] = useState('');

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ B2C: DEFAULT_SEG, B2B: DEFAULT_SEG });
  const [errors, setErrors] = useState({ B2C: {}, B2B: {} });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(emptyFilters);
  const [histLoading, setHistLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const seq = useRef(0);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getData('delivery-setting/view', null, authToken(), false);
      if (res?.setting) {
        setSettings({ B2C: { ...DEFAULT_SEG, ...res.setting.B2C }, B2B: { ...DEFAULT_SEG, ...res.setting.B2B } });
        setUpdatedAt(res.setting.updatedAt || null);
        setSaved(res.saved !== false);
      }
    } catch {
      setLoadError('Could not load the current settings.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadHistory = useCallback(async () => {
    const mine = ++seq.current;
    setHistLoading(true);
    try {
      const params = { page, limit: LIMIT, ...filters };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getData('delivery-setting/history', params, authToken(), false);
      if (mine !== seq.current) return;
      setHistory(res?.history || []);
      setUsers(res?.users || []);
      setMeta({ total: res?.meta?.total || 0, totalPages: res?.meta?.totalPages || 1 });
    } catch {
      if (mine === seq.current) setHistory([]);
    } finally {
      if (mine === seq.current) setHistLoading(false);
    }
  }, [page, filters]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const openEdit = () => {
    setForm({ B2C: { ...settings.B2C }, B2B: { ...settings.B2B } });
    setErrors({ B2C: {}, B2B: {} });
    setFormError('');
    setOpen(true);
  };
  const change = (seg) => (field, value) => {
    setForm((f) => ({ ...f, [seg]: { ...f[seg], [field]: value } }));
    setErrors((e) => ({ ...e, [seg]: { ...e[seg], [field]: undefined } }));
  };

  const save = async () => {
    setFormError('');
    const errs = { B2C: {}, B2B: {} };
    let bad = false;
    SEGMENTS.forEach(({ key }) => {
      ['codCharge', 'codThreshold', 'deliveryCharge', 'deliveryChargeRemovalThreshold'].forEach((f) => {
        const raw = form[key][f];
        const n = Number(raw);
        if (raw === '' || !Number.isFinite(n) || n < 0) { errs[key][f] = 'Enter 0 or more'; bad = true; }
      });
    });
    setErrors(errs);
    if (bad) return;
    setSaving(true);
    try {
      const body = {};
      SEGMENTS.forEach(({ key }) => {
        body[key] = { codAvailable: !!form[key].codAvailable };
        ['codCharge', 'codThreshold', 'deliveryCharge', 'deliveryChargeRemovalThreshold'].forEach((f) => { body[key][f] = Number(form[key][f]); });
      });
      const res = await postData('delivery-setting/update', body, authToken(), true);
      setOpen(false);
      setNotice(res?.message === 'No changes to save' ? 'Nothing changed.' : 'Settings saved. New orders use them straight away.');
      setPage(1);
      loadSettings();
      loadHistory();
    } catch (err) {
      setFormError(err?.message || 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  const setF = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const dirty = Object.values(filters).some(Boolean);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Shipping &amp; payment configuration</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Delivery charges and cash-on-delivery rules.{updatedAt ? ` Last changed ${dt(updatedAt)}.` : ''}</Typography>
        </Box>
        <Button startIcon={<MdEdit />} variant="contained" onClick={openEdit} disabled={loading} sx={yellowBtn}>Edit settings</Button>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}
      {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
      {!loading && !saved && <Alert severity="info" sx={{ mb: 2 }}>No settings have been saved yet, so the defaults are shown. Use Edit settings to set your own.</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        {loading ? [0, 1].map((i) => <Skeleton key={i} variant="rounded" height={300} sx={{ flex: 1, minWidth: 300, borderRadius: '14px' }} />) : SEGMENTS.map((s) => <SegmentCard key={s.key} seg={s} data={settings[s.key]} />)}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18 }}>Change history</Typography>
        <Tooltip title="Refresh"><span><IconButton onClick={loadHistory} disabled={histLoading} sx={{ background: '#f1f5f9', borderRadius: '10px' }}><MdRefresh size={20} /></IconButton></span></Tooltip>
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField select size="small" label="Customer type" value={filters.segment} onChange={(e) => setF('segment', e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="">B2C and B2B</MenuItem><MenuItem value="B2C">B2C only</MenuItem><MenuItem value="B2B">B2B only</MenuItem>
        </TextField>
        <TextField select size="small" label="Setting" value={filters.field} onChange={(e) => setF('field', e.target.value)} sx={{ minWidth: 210 }}>
          <MenuItem value="">All settings</MenuItem>
          {Object.entries(FIELD_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Changed by" value={filters.userId} onChange={(e) => setF('userId', e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="">Anyone</MenuItem>
          {users.map((u) => <MenuItem key={u._id} value={u._id}>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email}</MenuItem>)}
        </TextField>
        <TextField size="small" type="date" label="From" value={filters.from} onChange={(e) => setF('from', e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="To" value={filters.to} onChange={(e) => setF('to', e.target.value)} InputLabelProps={{ shrink: true }} />
        {dirty && <Button onClick={() => { setFilters(emptyFilters); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>When</TableCell>
                <TableCell sx={head}>Changed by</TableCell>
                <TableCell sx={head}>What changed</TableCell>
                <TableCell sx={head} align="right">Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {histLoading && [...Array(4)].map((_, i) => <TableRow key={i}>{[...Array(4)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!histLoading && history.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No changes recorded{dirty ? ' for these filters' : ' yet'}.</TableCell></TableRow>}
              {!histLoading && history.map((h) => (
                <TableRow key={h._id} hover>
                  <TableCell sx={cell}>{dt(h.date)}</TableCell>
                  <TableCell sx={cell}><b>{h.user || 'Unknown'}</b>{h.email && <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{h.email}</Typography>}</TableCell>
                  <TableCell sx={cell}>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {h.changes.slice(0, 3).map((c, i) => (
                        <Chip key={i} size="small" label={`${c.segment} · ${FIELD_LABEL[c.field] || c.field}`} sx={{ fontFamily: FONT, fontWeight: 600, height: 22, background: c.segment === 'B2C' ? '#e0e7ff' : '#e0f2fe', color: c.segment === 'B2C' ? '#3730a3' : '#075985' }} />
                      ))}
                      {h.changes.length > 3 && <Chip size="small" label={`+${h.changes.length - 3} more`} sx={{ height: 22, fontWeight: 700 }} />}
                      {h.changes.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#94a3b8' }}>No detail recorded</Typography>}
                    </Box>
                  </TableCell>
                  <TableCell align="right"><Tooltip title="View details"><IconButton size="small" onClick={() => setSelected(h)} sx={{ color: '#4f46e5', background: '#4f46e514', borderRadius: '8px' }}><MdVisibility size={17} /></IconButton></Tooltip></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} change{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* edit */}
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>Edit shipping &amp; payment settings</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>Applies to new orders as soon as you save.</Typography>
          </Box>
          <IconButton onClick={() => setOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {SEGMENTS.map((s) => (
              <Box key={s.key} sx={{ flex: 1, minWidth: 280, display: 'flex' }}><SegmentForm seg={s} value={form[s.key]} errors={errors[s.key]} onChange={change(s.key)} /></Box>
            ))}
          </Box>
          <Button size="small" onClick={() => setForm((f) => ({ ...f, B2B: { ...f.B2C } }))} sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}>Copy B2C values to B2B</Button>
          {formError && <Alert severity="error">{formError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
          <Button onClick={() => setOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : 'Save changes'}</Button>
        </DialogActions>
      </Dialog>

      {/* change details */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {selected && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>Settings change</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.9 }}>{dt(selected.date)}</Typography>
              </Box>
              <IconButton onClick={() => setSelected(null)} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #0ea5e9', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 0.5 }}>Who</Typography>
                <Row label="Changed by" value={selected.user || 'Unknown'} />
                <Row label="Email" value={selected.email || '—'} />
                <Row label="IP address" value={selected.ip || '—'} />
              </Box>
              <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: '4px solid #f59e0b', borderRadius: '14px', p: 2.25 }}>
                <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>What changed ({selected.changes.length})</Typography>
                {selected.changes.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No field-level detail was recorded for this entry.</Typography>}
                {selected.changes.map((c, i) => (
                  <Box key={i} sx={{ py: 1.1, borderBottom: i < selected.changes.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{c.segment} · {FIELD_LABEL[c.field] || c.field}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip size="small" label={showVal(c.field, c.from)} sx={{ fontWeight: 700, background: '#fee2e2', color: '#991b1b', textDecoration: 'line-through' }} />
                      <Typography sx={{ color: '#94a3b8' }}>→</Typography>
                      <Chip size="small" label={showVal(c.field, c.to)} sx={{ fontWeight: 800, background: '#dcfce7', color: '#166534' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setSelected(null)} variant="contained" sx={yellowBtn}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ShippingPaymentConfig;
